// src/components/modals/PendingOrdersGuiaModal.tsx
// Modal para mostrar la guía generada

import React from 'react'
import { CheckCircleIcon, EyeIcon, TruckIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface GuiaData {
  guia: string
  carrier?: string
  valor_flete?: number
  valor_costom?: number
  valor_otros?: number
  dias_entrega: number
  service?: string
  k_cobrados?: number
  cod_postaldestino?: string
  urlguia?: string
  trackUrl?: string
  orderUpdated?: boolean
  updateResponse?: {
    message?: string
  }
}

interface PendingOrdersGuiaModalProps {
  isOpen: boolean
  guiaData: GuiaData | null
  onClose: () => void
}

export const PendingOrdersGuiaModal: React.FC<PendingOrdersGuiaModalProps> = ({
  isOpen,
  guiaData,
  onClose
}) => {
  if (!isOpen || !guiaData) return null

  const handleCopyGuia = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(guiaData.guia)
        toast.success('Número de guía copiado al portapapeles', { icon: '📋', position: 'top-center' })
      } else {
        // Fallback para navegadores que no soportan Clipboard API
        const textArea = document.createElement('textarea')
        textArea.value = guiaData.guia
        textArea.style.position = 'fixed'
        textArea.style.left = '-999999px'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast.success('Número de guía copiado al portapapeles', { icon: '📋', position: 'top-center' })
      }
    } catch (error) {
      console.error('Error al copiar:', error)
      toast.error('Error al copiar el número de guía', { icon: '⚠️', position: 'top-center' })
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl p-8 m-4 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className=" ">Guía Generada</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {guiaData.orderUpdated && (
            <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">
                    <strong>Pedido actualizado correctamente:</strong> {guiaData.updateResponse?.message || 'La guía ha sido asignada al pedido'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Número de Guía:</p>
              <p className="text-xl font-bold text-blue-600">{guiaData.guia}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Transportadora:</p>
              <p className="text-xl font-bold text-blue-600">{guiaData.carrier || 'Envía'}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Valor Total del Flete:</p>
              <p className="text-xl font-bold text-blue-600">
                ${guiaData.carrier ? 
                  guiaData.valor_flete?.toLocaleString('es-CO') || '0' : 
                  ((guiaData.valor_flete || 0) + (guiaData.valor_costom || 0) + (guiaData.valor_otros || 0)).toLocaleString('es-CO')
                }
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-medium text-gray-700">Días de Entrega:</p>
              <p className="text-xl font-bold text-blue-600">{guiaData.dias_entrega} días</p>
            </div>
            {guiaData.carrier ? (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Servicio:</p>
                  <p className="text-lg font-semibold text-green-600">{guiaData.service || 'Estándar'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Estado:</p>
                  <p className="text-lg font-semibold text-green-600">Generada</p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Peso Cobrado:</p>
                  <p className="text-xl font-bold text-blue-600">{guiaData.k_cobrados} kg</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Valor Flete Base:</p>
                  <p className="text-lg font-semibold text-green-600">${guiaData.valor_flete?.toLocaleString('es-CO') || '0'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Valor Costo M:</p>
                  <p className="text-lg font-semibold text-green-600">${guiaData.valor_costom?.toLocaleString('es-CO') || '0'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Valor Otros:</p>
                  <p className="text-lg font-semibold text-green-600">${guiaData.valor_otros?.toLocaleString('es-CO') || '0'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Código Postal Destino:</p>
                  <p className="text-lg font-semibold text-gray-600">{guiaData.cod_postaldestino || 'No disponible'}</p>
                </div>
              </>
            )}
          </div>

          {guiaData.urlguia && (
            <div className="flex justify-center space-x-4">
              <a 
                href={guiaData.urlguia} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-semibold"
              >
                <EyeIcon className="h-5 w-5 text-white" />
                Ver Guía
              </a>
              {guiaData.trackUrl && (
                <a 
                  href={guiaData.trackUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 font-semibold"
                >
                  <TruckIcon className="h-5 w-5 text-white" />
                  Rastrear Envío
                </a>
              )}
              <button
                onClick={handleCopyGuia}
                className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-semibold"
              >
                <ClipboardIcon className="h-5 w-5 text-white" />
                Copiar Guía
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

